import React, { FC, useMemo, useState } from 'react';
import { Box, CircularProgress, Container, Dialog, DialogContent, IconButton, Paper, TextField, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import { useGetEncountersQuery, useGetEncounterRelatedQuery } from '../healthWalletSlice';
import type { EncounterVisit } from '../healthWalletSlice';
import { formatDate } from '../healthWalletConstants';
import type { SectionProps } from './TabRenderer';
import { encounterTypeKey, encounterTypeMeta, isVisitUpcoming, ENCOUNTER_TYPE_META, ENCOUNTER_TYPE_ORDER } from './classify';
import type { EncounterTypeKey } from './classify';
import { V1_ACCENT, V1_ACCENT_TINT, V1_ACCENT_BORDER } from './theme';
import { V1SearchField } from './V1SearchField';

// ============================================================================
// Encounters tab sections (design: Polygon Health Trends Redesign/Encounters
// Desktop.dc.html, in the DESIGN palette). CHRONOLOGICAL shape — data =
// get_patient_encounters (one row per COLLAPSED visit: same-day copies across
// Epic/CCDA sources folded, shadow-suppressed context-free rows absorbed) —
// year sections, newest first, first two years open (design). Detail dialog
// lives HERE (EncounterVisit ≠ the shared GroupedSummary contract); related
// items fetch lazily per dialog via the visit's collapsed fhir_ids. Cross-tab
// jump links from related items = DEFERRED (names shown without links).
// ============================================================================

const PAGE_WIDTH_SX = { maxWidth: { xs: '100%', sm: '94%' } };

const visitDateLabel = (v: EncounterVisit): string => {
  const end = v.end ? v.end.slice(0, 10) : null;
  return end && end !== v.day ? `${formatDate(v.day)} – ${formatDate(end)}` : formatDate(v.day);
};

const linkedLabel = (v: EncounterVisit): string => {
  const l = v.linked ?? {};
  const parts: string[] = [];
  if (l.conditions) parts.push(`${l.conditions} ${l.conditions === 1 ? 'diagnosis' : 'diagnoses'}`);
  if (l.procedures) parts.push(`${l.procedures} ${l.procedures === 1 ? 'procedure' : 'procedures'}`);
  if (l.medications) parts.push(`${l.medications} medication${l.medications === 1 ? '' : 's'}`);
  if (l.labs) parts.push(`${l.labs.toLocaleString('en-US')} lab result${l.labs === 1 ? '' : 's'}`);
  if (l.reports) parts.push(`${l.reports} report${l.reports === 1 ? '' : 's'}`);
  return parts.length ? `Linked: ${parts.join(' · ')}` : '';
};

// --- 1. Hero -----------------------------------------------------------------
export const EncountersHero: FC<SectionProps> = () => {
  const { data, isLoading } = useGetEncountersQuery();
  // The hero heads the HISTORICAL tab (upcoming visits get their own tab/hero), so count over the same
  // historical set the body shows — keeps the headline count + type tiles consistent with the sections.
  const visits = (data ?? []).filter((v) => !isVisitUpcoming(v));
  if (isLoading) {
    return <Container maxWidth={false} sx={{ ...PAGE_WIDTH_SX, mt: 2, display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress sx={{ color: V1_ACCENT }} /></Container>;
  }
  const orgs = new Set(visits.map((v) => v.org).filter(Boolean)).size;
  // Per visit-TYPE counts — the hero tiles mirror the filter pills (and the card type badges), present-only,
  // instead of the old aggregate "Visits / Organizations / Visit types" metrics (org count kept in the text).
  const typeCounts = visits.reduce((acc, v) => { const k = encounterTypeKey(v); acc[k] = (acc[k] ?? 0) + 1; return acc; }, {} as Record<EncounterTypeKey, number>);
  const firstYear = visits.length ? visits[visits.length - 1].day.slice(0, 4) : '';
  const lastYear = visits.length ? visits[0].day.slice(0, 4) : '';
  const tile = (value: number | string, label: string, bg: string, fg: string) => (
    <Box sx={{ width: { xs: 92, sm: 104 }, flexShrink: 0, bgcolor: bg, borderRadius: '15px', p: '16px 10px', textAlign: 'center' }}>
      <Typography sx={{ fontSize: { xs: 20, sm: 28 }, fontWeight: 700, color: fg, lineHeight: 1 }}>{value}</Typography>
      <Typography sx={{ fontSize: 11, color: fg, mt: 0.75, fontWeight: 600 }}>{label}</Typography>
    </Box>
  );
  return (
    <Container maxWidth={false} sx={{ ...PAGE_WIDTH_SX, mt: 2 }}>
      <Paper elevation={0} sx={{ borderRadius: '20px', p: '24px 26px', border: `1px solid ${V1_ACCENT_BORDER}`, bgcolor: 'white', display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1, minWidth: 300 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.5, color: V1_ACCENT, textTransform: 'uppercase', mb: 1.25 }}>
            Your record at a glance
          </Typography>
          <Typography sx={{ fontSize: 21, fontWeight: 600, lineHeight: 1.4, letterSpacing: '-0.2px', maxWidth: 580 }}>
            Your record documents <strong>{visits.length.toLocaleString('en-US')}</strong> visits
            {orgs ? <> across <strong>{orgs}</strong> {orgs === 1 ? 'organization' : 'organizations'}</> : null}
            {firstYear ? <> between <strong>{firstYear}</strong> and <strong>{lastYear}</strong></> : null}.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          {ENCOUNTER_TYPE_ORDER.filter((k) => (typeCounts[k] ?? 0) > 0).map((k) => (
            <React.Fragment key={k}>{tile(typeCounts[k], ENCOUNTER_TYPE_META[k].label, ENCOUNTER_TYPE_META[k].bg, ENCOUNTER_TYPE_META[k].fg)}</React.Fragment>
          ))}
        </Box>
      </Paper>
    </Container>
  );
};

// --- 2. Explainer -------------------------------------------------------------
export const EncountersExplainer: FC<SectionProps> = () => (
  <Container maxWidth={false} sx={{ ...PAGE_WIDTH_SX, mt: 2 }}>
    <Box sx={{ bgcolor: V1_ACCENT_TINT, borderRadius: '16px', p: '16px 20px', display: 'flex', gap: 1.75, alignItems: 'flex-start' }}>
      <Box sx={{ flex: 'none', width: 26, height: 26, borderRadius: '50%', bgcolor: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <InfoOutlinedIcon sx={{ color: V1_ACCENT, fontSize: 18 }} />
      </Box>
      <Box>
        <Typography sx={{ fontSize: 14, fontWeight: 650, mb: 0.4 }}>About your visits</Typography>
        <Typography sx={{ fontSize: 13, color: '#4a4a52', lineHeight: 1.5, maxWidth: 820 }}>
          Each card is one documented visit — office appointments, labs, infusions, imaging, and hospital stays. The same
          visit is often documented several times across your providers&rsquo; systems; we fold those copies into one card and
          show the count. This page presents your record as documented and does not provide medical advice.
        </Typography>
      </Box>
    </Box>
  </Container>
);

// --- Timeline row (design: vertical chronological rail, thick type-colored dot per visit) --------
const VisitRow: FC<{ v: EncounterVisit; onOpen: () => void }> = ({ v, onOpen }) => {
  const meta = encounterTypeMeta(v);
  const linked = linkedLabel(v);
  const title = v.org || v.provider || meta.label;
  const providerLine = [v.provider, v.type && v.type !== title ? v.type : null].filter(Boolean).join(' · ');
  return (
    <Box sx={{ display: 'flex', gap: 1.75, alignItems: 'flex-start' }}>
      {/* Type-colored dot, sitting on the section's rail */}
      <Box sx={{ flex: 'none', width: 12, height: 12, borderRadius: '50%', bgcolor: meta.fg, mt: 1.9, zIndex: 1, boxShadow: '0 0 0 3px white' }} />
      <Box onClick={onOpen} sx={{
        flex: 1, minWidth: 0, bgcolor: 'white', border: '1px solid #ece9f4', borderRadius: '14px', p: '14px 18px',
        cursor: 'pointer', '&:hover': { borderColor: V1_ACCENT_BORDER, bgcolor: '#fdfcff' },
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1.25 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ flex: 'none', fontSize: '10.5px', fontWeight: 700, px: 1.25, py: 0.5, borderRadius: '20px', bgcolor: meta.bg, color: meta.fg, whiteSpace: 'nowrap' }}>
              {meta.label}
            </Box>
            <Typography sx={{ fontSize: 13, color: '#6b6b70' }}>{visitDateLabel(v)}</Typography>
          </Box>
          <Typography sx={{ fontSize: 12, color: '#8e8e93', flex: 'none' }}>
            {v.status === 'finished' ? 'Completed' : (v.status || '')}
          </Typography>
        </Box>
        <Typography sx={{ fontSize: 16, fontWeight: 700, letterSpacing: '-0.2px', mt: 1, lineHeight: 1.25 }}>{title}</Typography>
        <Typography sx={{ fontSize: 13, color: '#6b6b70', mt: 0.25, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {providerLine || 'Provider not documented'}
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1.25, pt: 1.25, borderTop: '1px solid #f0eef6' }}>
          <Typography sx={{ fontSize: 12, color: '#6b6b70' }}>
            {linked || `${v.records.toLocaleString('en-US')} record${v.records === 1 ? '' : 's'}`}
          </Typography>
          <Typography sx={{ fontSize: '12.5px', fontWeight: 600, color: V1_ACCENT, whiteSpace: 'nowrap' }}>View visit ›</Typography>
        </Box>
      </Box>
    </Box>
  );
};

// --- Detail dialog --------------------------------------------------------------
const RELATED_CATS: Array<{ key: keyof NonNullable<EncounterVisit['linked']>; title: string }> = [
  { key: 'conditions', title: 'Diagnoses documented' },
  { key: 'procedures', title: 'Procedures performed' },
  { key: 'medications', title: 'Medications' },
  { key: 'labs', title: 'Laboratory results' },
  { key: 'reports', title: 'Reports' },
];

const VisitDetailDialog: FC<{ visit: EncounterVisit | null; onClose: () => void }> = ({ visit, onClose }) => {
  const { data: related, isLoading } = useGetEncounterRelatedQuery(
    { ids: visit?.ids ?? [] },
    { skip: !visit || !(visit.ids ?? []).length }
  );
  if (!visit) return null;
  const meta = encounterTypeMeta(visit);
  const end = visit.end ? visit.end.slice(0, 10) : null;
  const multiDay = !!end && end !== visit.day;
  const fact = (label: string, value: string, bordered = false) => (
    <Box sx={{ flex: 1, minWidth: 120, ...(bordered ? { borderLeft: '1px solid #f0f0f3', pl: 2 } : {}) }}>
      <Typography sx={{ fontSize: 11, color: '#aeaeb2', fontWeight: 600 }}>{label}</Typography>
      <Typography sx={{ fontSize: 15, fontWeight: 700, mt: 0.5 }}>{value}</Typography>
    </Box>
  );
  return (
    <Dialog open={!!visit} onClose={onClose} maxWidth="md" fullWidth transitionDuration={0}
      PaperProps={{ sx: { borderRadius: '22px', bgcolor: '#f7f5fb', overflow: 'hidden' } }}>
      <Box sx={{ bgcolor: 'white', p: '24px 26px', borderBottom: '1px solid #ece9f4', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2.5 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 25, fontWeight: 700, letterSpacing: '-0.5px', lineHeight: 1.15 }}>{visitDateLabel(visit)}</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.25 }}>
            <Box sx={{ fontSize: '12.5px', fontWeight: 700, px: 1.5, py: 0.6, borderRadius: '20px', bgcolor: meta.bg, color: meta.fg }}>{meta.label}</Box>
            <Typography sx={{ fontSize: '12.5px', color: '#8e8e93' }}>as documented in your health record</Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small" aria-label="Close" sx={{ bgcolor: '#ececed', color: '#6b6b70' }}><CloseIcon fontSize="small" /></IconButton>
      </Box>

      <DialogContent sx={{ p: '22px !important' }}>
        {/* Overview facts */}
        <Paper elevation={0} sx={{ borderRadius: '16px', p: '18px 20px', mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', bgcolor: 'white' }}>
          {fact('Organization', visit.org || 'Not documented')}
          {fact('Provider', visit.provider || 'Not documented', true)}
          {fact('Status', visit.status === 'finished' ? 'Completed' : (visit.status || 'Not documented'), true)}
          {fact('Documented records', visit.records.toLocaleString('en-US'), true)}
        </Paper>

        {/* Stay timeline (multi-day only — dates are what the record carries) */}
        {multiDay && (
          <Paper elevation={0} sx={{ borderRadius: '16px', p: '18px 20px', mb: 2, bgcolor: 'white', display: 'flex', gap: 2 }}>
            {fact('Check-in', formatDate(visit.day))}
            {fact('Discharge', formatDate(end as string), true)}
          </Paper>
        )}

        {/* Related items during this visit (names, no cross-tab links yet — deferred). */}
        <Paper elevation={0} sx={{ borderRadius: '16px', p: '18px 20px', mb: 2, bgcolor: 'white' }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.3, color: '#6b6b70', textTransform: 'uppercase', mb: 1.5 }}>
            Documented during this visit
          </Typography>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={22} sx={{ color: V1_ACCENT }} /></Box>
          ) : RELATED_CATS.some((c) => (related?.[c.key] ?? []).length > 0) ? (
            RELATED_CATS.map((c) => {
              const names = related?.[c.key] ?? [];
              if (!names.length) return null;
              const total = visit.linked?.[c.key];
              return (
                <Box key={c.key} sx={{ mb: 1.5 }}>
                  <Typography sx={{ fontSize: '12.5px', fontWeight: 700, color: '#3a3a3c' }}>
                    {c.title}{total && total > names.length ? <Box component="span" sx={{ color: '#aeaeb2', fontWeight: 600 }}> · top {names.length} of {total.toLocaleString('en-US')}</Box> : null}
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: '#6b6b70', lineHeight: 1.6 }}>{names.join(' · ')}</Typography>
                </Box>
              );
            })
          ) : (
            <Typography sx={{ fontSize: 13, color: '#8e8e93' }}>No linked records reference this visit.</Typography>
          )}
        </Paper>

        {/* Technical details */}
        <Paper elevation={0} sx={{ borderRadius: '16px', p: '18px 20px', bgcolor: 'white' }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.3, color: '#6b6b70', textTransform: 'uppercase', mb: 1 }}>Technical details</Typography>
          <Typography sx={{ fontSize: 13, color: '#6b6b70', lineHeight: 1.7 }}>
            FHIR resource: Encounter · Class: {visit.classCode || '—'} · Source type: {visit.type || '—'}
            {visit.status ? <> · Status: {visit.status}</> : null} · Collapsed copies: {visit.records}
          </Typography>
        </Paper>
      </DialogContent>
    </Dialog>
  );
};

// --- 3. Body: search + type pills + year sections + internal dialog -------------
// `upcomingOnly` (default false) flips the visit set: historical Encounters shows
// only past visits (upcoming ones move to the Upcoming tab); the Upcoming tab
// reuses THIS component with upcomingOnly to render the same cards for future
// appointments (RTK dedupes the shared encounters read).
export const EncountersBody: FC<SectionProps & { upcomingOnly?: boolean }> = ({ upcomingOnly }) => {
  const { data, isLoading } = useGetEncountersQuery();
  const visits = (data ?? []).filter((v) => (upcomingOnly ? isVisitUpcoming(v) : !isVisitUpcoming(v)));
  const [filter, setFilter] = useState<'all' | EncounterTypeKey>('all');
  const [search, setSearch] = useState('');
  const [openYears, setOpenYears] = useState<Record<string, boolean>>({});
  const [selected, setSelected] = useState<EncounterVisit | null>(null);

  const q = search.trim().toLowerCase();
  const matches = (v: EncounterVisit) =>
    !q || [v.org, v.provider, v.type, encounterTypeMeta(v).label].filter(Boolean).some((s) => (s as string).toLowerCase().includes(q));

  const presentTypes = useMemo(() => {
    const set = new Set<EncounterTypeKey>();
    visits.forEach((v) => set.add(encounterTypeKey(v)));
    return set;
  }, [visits]);

  if (isLoading) return null; // hero shows the spinner

  const shown = visits.filter((v) => (filter === 'all' || encounterTypeKey(v) === filter) && matches(v));
  const years: string[] = [];
  const byYear: Record<string, EncounterVisit[]> = {};
  shown.forEach((v) => {
    const y = v.day.slice(0, 4);
    if (!byYear[y]) { byYear[y] = []; years.push(y); }
    byYear[y].push(v);
  });

  const pill = (key: 'all' | EncounterTypeKey, label: string) => (
    <Box key={key} onClick={() => setFilter(key)} sx={{
      fontSize: 13, fontWeight: 600, px: 2, py: 1, borderRadius: '20px', cursor: 'pointer',
      bgcolor: filter === key ? V1_ACCENT : 'white', color: filter === key ? 'white' : '#3a3a3c',
      border: `1px solid ${filter === key ? V1_ACCENT : '#e0e0e6'}`,
    }}>{label}</Box>
  );
  const typePill = (key: EncounterTypeKey, label: string) => (presentTypes.has(key) ? pill(key, label) : null);

  const forced = !!q || filter !== 'all';

  return (
    <Container maxWidth={false} sx={{ ...PAGE_WIDTH_SX, mt: 2.5, pb: 12 }}>
      <V1SearchField value={search} onChange={setSearch} placeholder="Search visits (provider, organization, type)" />
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
        {pill('all', 'All')}
        {ENCOUNTER_TYPE_ORDER.map((k) => typePill(k, ENCOUNTER_TYPE_META[k].label))}
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }}>
        {years.map((y, i) => {
          const isOpen = forced ? true : (openYears[y] !== undefined ? openYears[y] : i < 2); // first 2 years open (design)
          const list = byYear[y];
          return (
            <Paper key={y} elevation={0} sx={{ borderRadius: '18px', border: `1px solid ${V1_ACCENT_BORDER}`, bgcolor: 'white', overflow: 'hidden' }}>
              <Box onClick={() => setOpenYears((s) => ({ ...s, [y]: !(s[y] !== undefined ? s[y] : i < 2) }))}
                sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: '18px 22px', cursor: 'pointer' }}>
                <Typography component="h2" sx={{ m: 0, fontSize: 18, fontWeight: 700, letterSpacing: '-0.3px', flex: 1 }}>{y}</Typography>
                <Box sx={{ fontSize: '12.5px', fontWeight: 700, color: '#6b6b70', bgcolor: '#f0f0f3', px: 1.4, py: 0.5, borderRadius: '20px' }}>
                  {list.length} visit{list.length === 1 ? '' : 's'}
                </Box>
                <Typography sx={{ fontSize: 13, color: '#8e8e93', width: 16, textAlign: 'center' }}>{isOpen ? '▾' : '▸'}</Typography>
              </Box>
              {isOpen && (
                <Box sx={{ px: 2.75, pb: 2.75 }}>
                  {/* Vertical chronological rail — the dots (VisitRow) sit on this line. */}
                  <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: 1.75,
                             '&::before': { content: '""', position: 'absolute', left: 5, top: 10, bottom: 10, width: '2px', bgcolor: '#ececf1' } }}>
                    {list.map((v, j) => <VisitRow key={`${v.day}-${j}`} v={v} onOpen={() => setSelected(v)} />)}
                  </Box>
                </Box>
              )}
            </Paper>
          );
        })}
      </Box>
      {years.length === 0 && (
        <Typography sx={{ p: '40px 26px', textAlign: 'center', color: '#8e8e93', fontSize: 15 }}>
          No visits match &ldquo;{search}&rdquo;.
        </Typography>
      )}
      <Box sx={{ mt: 4.5, pt: 2.75, borderTop: '1px solid #e2e2e8', textAlign: 'center' }}>
        <Typography sx={{ fontSize: 12, color: '#aeaeb2', lineHeight: 1.55, maxWidth: 660, mx: 'auto' }}>
          This page lists the visits documented by your healthcare providers. It presents your health record as documented
          and does not provide medical advice.
        </Typography>
      </Box>
      <VisitDetailDialog visit={selected} onClose={() => setSelected(null)} />
    </Container>
  );
};
